import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import AzilisSwitch3, { AzilisSwitch } from "../../components/Switch";
import PlusIcon from "../../components/SideTableIcons/PlusIcon";
import SaveIcon from "../../components/SideTableIcons/SaveIcon";
import MailIcon from "../../components/SideTableIcons/MailIcon";
import EditableResultDropdownAutocomplete from "../../components/EditableResultDropdownAutocomplete";
import EditableAutocompleteNonCategory from "../../components/EditableAutocompleteNonCategory";
import EditableResultDropdownSubgroups from "../../components/EditableDropdownSubgroups";
import MultipleCustomColumn from "./components/MultipleCustomColumn";
import { Checkbox, FormControlLabel } from "@mui/material";
import { useUserAccess } from "../../components/WriteAccessContext";
import BuyerInput from "../../components/BuyerInput/BuyerInput";
import ApiAutocompleteDropdown from "../../components/ApiAutocompleteDropdown";
import EditableMultiSelectAutocomplete from "../../components/EditableMultiSelectDropdown";

const AddNewItemRow = ({
  columns,
  onAdd,
  onAddRow,
  customInputs,
  dropdownData,
  hiddenColumns,
  handleValidation,
  disableRules,
  noShow,
  dynamicPlaceholders,
  newRows,
  setOpen,
}) => {
  const [newItemData, setNewItemData] = useState({});
  const { userAccess } = useUserAccess();
  const [naStates, setNaStates] = useState({});
  const [showNewRow, setShowNewRow] = useState(false);

  useEffect(() => {
    const defaultValues = columns.reduce((acc, column) => {
      if (column.type === "switch") {
        acc[column.accessor] = column.choice1 || "Yes";
      }
      return acc;
    }, {});

    setNewItemData((prev) => ({ ...prev, ...defaultValues }));
  }, [columns]);

  const handleInputChange = (columnId, value) => {
    setNewItemData((prev) => {
      const updatedData = { ...prev, [columnId]: value };

      const placeholderConfig =
        dynamicPlaceholders && dynamicPlaceholders[columnId];
      if (placeholderConfig) {
        const { targetField, getPlaceholder } = placeholderConfig;
        const placeholderValue = getPlaceholder(
          value?.value || value,
          dropdownData[columnId],
          updatedData
        );
        if (placeholderValue !== undefined) {
          updatedData[targetField] = placeholderValue;
        }
      }

      return updatedData;
    });
  };

  const handleNaClick = (columnId) => {
    setNaStates((prev) => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));

    setNewItemData((prev) => ({
      ...prev,
      [columnId]: prev[columnId] && !naStates[columnId] ? prev[columnId] : "",
    }));
  };

  const resetFormData = () => {
    // Reset to default values for switches, empty for others
    const defaultValues = columns.reduce((acc, column) => {
      if (column.type === "switch") {
        acc[column.accessor] = column.choice1 || "Yes";
      } else {
        acc[column.accessor] = "";
      }
      return acc;
    }, {});

    setNewItemData(defaultValues);
    setNaStates({});
  };

  const handleAdd = () => {
    if (handleValidation(newItemData)) {
      onAdd(newItemData);
      resetFormData();
      setShowNewRow(false);
    }
  };

  const handleAddNewRow = () => {
    setShowNewRow(!showNewRow);
    if (showNewRow) {
      resetFormData();
    }
  };

  const renderInput = (column) => {
    if (hiddenColumns.includes(column.accessor)) {
      return null;
    }
    const isDisabled =
      (disableRules && disableRules[column.accessor]
        ? disableRules[column.accessor](newItemData)
        : false) || naStates[column.accessor];

    const isNotShown =
      noShow && noShow[column.accessor]
        ? noShow[column.accessor](newItemData)
        : false;

    const inputStyle = {
      maxWidth: "100%",
      whiteSpace: "nowrap",
      textAlign: "center",
      opacity: isDisabled ? 0.5 : 1,
      pointerEvents: isDisabled ? "none" : "auto",
    };

    const nonDropdownInputStyle = {
      ...inputStyle,
      height: "28px",
    };

    const dropdownInputStyle = {
      ...inputStyle,
      display: "flex",
      alignItems: "center",
    };

    const naBtnStyle = {
      marginLeft: "5px",
      color: "white",
      opacity: naStates[column.accessor] ? 1 : 0.5,
      cursor: "pointer",
      padding: "3px",
    };

    switch (column.type) {
      case "text":
      case "number":
        const isHidden =
          column.isNA &&
          column.isNA === true &&
          Object.keys(naStates).length > 0 &&
          Object.keys(naStates)?.[0] === column.accessorNA &&
          Object.values(naStates)?.[0] === true
            ? true
            : column.isNA === false &&
              Object.keys(naStates).length > 0 &&
              Object.keys(naStates)?.[0] === column.accessorNA &&
              Object.values(naStates)?.[0] === false
            ? true
            : false;
        return (
          !isHidden && (
            <div
              style={{
                display: "flex",
                alignItems: "stretch",
                justifyContent: "center",
              }}
            >
              {" "}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  width: "100%",
                }}
              >
                <input
                  type={column.type}
                  value={newItemData[column.accessor] || ""}
                  onChange={(e) => {
                    let value = e.target.value;

                    if (column.type === "number") {
                      // Allow empty string or valid decimal inputs
                      if (value === "" || /^-?\d*\.?\d*$/.test(value)) {
                        // Convert to number only when needed for validation
                        const numValue = value === "" ? "" : Number(value);

                        if (value !== "" && !isNaN(numValue)) {
                          // Apply min/max constraints after conversion
                          if (column.minValue !== undefined) {
                            value = Math.max(
                              column.minValue,
                              numValue
                            ).toString();
                          }
                          if (column.maxValue !== undefined) {
                            value = Math.min(
                              column.maxValue,
                              numValue
                            ).toString();
                          }
                        }

                        handleInputChange(column.accessor, value);
                      }
                    } else {
                      handleInputChange(column.accessor, value);
                    }
                  }}
                  placeholder={column.placeholder}
                  style={{
                    ...nonDropdownInputStyle,
                    width: column.isNABtn ? "calc(100% - 5px)" : column.width,
                    marginRight: column.isNABtn ? "5px" : "0",
                  }}
                  disabled={isDisabled}
                />
                <>
                  {column.unit && typeof column.unit === "function" ? (
                    <span style={{ marginLeft: "5px", color: "#193666" }}>
                      {column.unit("new")}
                    </span>
                  ) : column.unit ? (
                    <span style={{ marginLeft: "5px", color: "#193666" }}>
                      {column.unit}
                    </span>
                  ) : null}
                </>
              </div>
              <>
                {column.isNABtn && (
                  <span
                    className="na-btn-input"
                    style={naBtnStyle}
                    onClick={() => handleNaClick(column.accessor)}
                  >
                    n/a
                  </span>
                )}
              </>
            </div>
          )
        );
      case "dropdown":
        const options = dropdownData[column.accessor] || [];
        if (isNotShown === true) return;
        if (column.isMultiSelect) {
          return (
            <div
              style={{
                ...dropdownInputStyle,
                display: "flex",
                alignItems: "center",
              }}
            >
              <EditableMultiSelectAutocomplete
                chosen={newItemData[column.accessor]}
                setValue={(value) => handleInputChange(column.accessor, value)}
                options={options.map((content) => ({
                  title: content.name,
                  value: content.value,
                  name: content.name,
                }))}
                edit={true}
                disabled={isDisabled}
              />
            </div>
          );
        } else if (column.isSubgroup) {
          return (
            <div
              style={{
                ...dropdownInputStyle,
                display: "flex",
                alignItems: "center",
              }}
            >
              <EditableResultDropdownSubgroups
                chosen={newItemData[column.accessor]}
                setValue={(value) => handleInputChange(column.accessor, value)}
                options={options.map((content) => ({
                  subgroup: content.subgroup,
                  subgroup_en: content.subgroup_en,
                  title: content.name,
                  value: content.value,
                  name: content.name,
                }))}
                edit={true}
                disabled={isDisabled}
              />
            </div>
          );
        } else if (column.isCategory) {
          return (
            <div
              style={{
                ...dropdownInputStyle,
                display: "flex",
                alignItems: "center",
              }}
            >
              <EditableResultDropdownAutocomplete
                chosen={newItemData[column.accessor]}
                setValue={(value) => handleInputChange(column.accessor, value)}
                options={options.map((content) => ({
                  category: content.category,
                  title: content.name,
                  value: content.value,
                  name: content.name,
                }))}
                edit={true}
                disabled={isDisabled}
              />
            </div>
          );
        } else {
          return (
            <div
              style={{
                ...dropdownInputStyle,
                display: "flex",
                alignItems: "center",
              }}
            >
              <EditableAutocompleteNonCategory
                chosen={newItemData[column.accessor]}
                setValue={(value) => handleInputChange(column.accessor, value)}
                options={options.map((content) => ({
                  title: content.name,
                  value: content.value,
                  name: content.name,
                }))}
                edit={true}
                disabled={isDisabled}
              />
            </div>
          );
        }
      case "apiAutocomplete":
        return (
          <div
            style={{
              ...dropdownInputStyle,
              display: "flex",
              alignItems: "center",
            }}
          >
            <ApiAutocompleteDropdown
              chosen={newItemData[column.accessor]}
              setValue={(value) => handleInputChange(column.accessor, value)}
              disabled={isDisabled}
              edit={true}
            />
          </div>
        );
      case "switch":
        return (
          <AzilisSwitch
            key={hiddenColumns.join(",")}
            choice1={column.choice1 || "Yes"}
            choice2={column.choice2 || "No"}
            which={newItemData[column.accessor]}
            setWhich={(value) => handleInputChange(column.accessor, value)}
            styleNo={column.styleNo || 6}
            disabled={isDisabled}
          />
        );
      case "switch3":
        return (
          <AzilisSwitch3
            key={hiddenColumns.join(",")}
            choice1={column.choice1 || "Yes"}
            choice2={column.choice2 || "No"}
            choice3={column.choice3}
            value1={column.choice1 || "Yes"}
            value2={column.choice2 || "No"}
            value3={column.choice3}
            which={newItemData[column.accessor]}
            setWhich={(value) => handleInputChange(column.accessor, value)}
            styleNo={column.styleNo || 6}
            disabled={isDisabled}
          />
        );
      case "custom":
        const CustomInput = customInputs[column.accessor];
        return (
          <CustomInput
            value={newItemData[column.accessor]}
            onChange={(value) => handleInputChange(column.accessor, value)}
            style={nonDropdownInputStyle}
          />
        );
      case "multipleCustom":
        return (
          <MultipleCustomColumn
            customInputs={column.customInputs}
            renderInput={(customInput) =>
              renderInput({
                ...customInput,
                accessor: `${column.accessor}_${customInput.accessor}`,
              })
            }
            layout={column.layout}
            naStates={naStates}
          />
        );
      case "buyerInput":
        return (
          <BuyerInput
            buyerOptions={column.buyerOptions}
            value={newItemData[column.accessor]}
            onChange={(value) => handleInputChange(column.accessor, value)}
            disabled={isDisabled}
          />
        );
      case "checkbox":
        return (
          <FormControlLabel
            control={<Checkbox color="success" />}
            checked={newItemData[column.accessor] || false}
            onChange={(e) =>
              handleInputChange(column.accessor, e.target.checked)
            }
            sx={{ marginRight: 0, marginLeft: "-3px" }}
            disabled={isDisabled}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {showNewRow && (
        <tr>
          {columns.map((column) => (
            <td
              key={column.accessor}
              style={{
                width:
                  column.type === "spacer"
                    ? column.width
                    : hiddenColumns.includes(column.accessor)
                    ? "30px"
                    : "auto",
                padding:
                  column.type === "spacer"
                    ? "0"
                    : hiddenColumns.includes(column.accessor)
                    ? "0 5px"
                    : "8px",
              }}
              className={
                column.type === "spacer"
                  ? "spacer-column"
                  : "td-with-background"
              }
            >
              {column.type !== "spacer" && renderInput(column)}
            </td>
          ))}
          <td>
            {userAccess === "write" && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <SaveIcon style={{ cursor: "pointer" }} onClick={handleAdd} />
                <MailIcon
                  style={{ width: "15px", cursor: "pointer" }}
                  onClick={() => {
                    setOpen(true);
                  }}
                />
              </div>
            )}
          </td>
        </tr>
      )}
      {!newRows && (
        <tr className="bg-add-new-row">
          <td colSpan={columns.length + 1}>
            <PlusIcon style={{ cursor: "pointer" }} onClick={handleAddNewRow} />
          </td>
        </tr>
      )}
    </>
  );
};

AddNewItemRow.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  onAdd: PropTypes.func.isRequired,
  customInputs: PropTypes.objectOf(PropTypes.func),
  dropdownData: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.object)),
  hiddenColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default AddNewItemRow;
